define("Pages/Group", [`Page`, `module`], function (Super, Self) {
	const SCROLLING_LAYOUTS = [`FLYER`, `GRID`, `LIST`];
	const DEFAULT_THEME = `DARK`;
	const EVENTS_PER_PAGE = 10;

	return Super.inherit({
		namespace: Self.id,
		requires: [`Groups`, `XHR`],
		constructor(setup) {
			const { Config, Groups, container, data } = this;
			const { PRIMARY_DOMAIN } = Config;
			const { trackingOptions } = data;
			const expanding = true; // whether new events are currently being fetched
			let { groupId } = setup;
			let promise;

			promise = Groups.get({
				id: groupId,
				then: (group) => {
					const { id, layout, subdomain } = group;
					const theme = this.isString(group.theme, 1) ? group.theme : DEFAULT_THEME;
					const url = `${PRIMARY_DOMAIN}/groups/${subdomain}`;
					const title = `${group.name} Tickets & Events | Tixr`;
					const paginatedLayouts = [`GRID`, `LIST`];
					const promises = [];
					let moreEventsAvailable;
					let currentPage;

					trackingOptions.pageType = `Group`;
					trackingOptions.groupId = group.id;
					data.uid = `g.${group.id}`;

					if (this.includes(paginatedLayouts, group.layout)) {
						moreEventsAvailable = true;
						currentPage = 1;
					} else {
						moreEventsAvailable = false;
						currentPage = null;
					}

					container.setAttribute(`theme`, theme);
					data.title = title;

					this.assign(data, {
						EVENTS_PER_PAGE,
						PRIMARY_DOMAIN,
						currentPage,
						expanding,
						group,
						moreEventsAvailable,
						theme,
						url,
					});

					if (layout === `CALENDAR`) {
						// async handler
						const then = (pages) => {
							data.mode = this.isArray(pages, 1) ? `calendar` : `empty`;
						};
						// fetch the group's calendar
						const calendarPromise = Groups.getCalendarPages({ id, then });
						promises.push(calendarPromise);
					} else {
						const layoutMode = layout === `CAROUSEL` ? `carousel` : `default`;

						// async handler
						const then = (events) => {
							data.mode = this.isArray(events) ? layoutMode : `empty`;
							data.events = events;
						};
						// fetch the first batch of events
						const eventsPromise = this.getMoreEvents({ then });
						promises.push(eventsPromise);
					}

					return this.when({ promises });
				},
			});

			return this.when({
				promise,
				then: () => {
					const { events, group, mode, url } = data;
					const { backgroundUrl, hiddenDetails, mobileBackgroundUrl } = group;
					const showPrices = group.priceKey === `SHOW`;
					const promises = [];
					const backupBackgroundUrl = `${Config.STATIC_IMG_PATH}/group-bg.jpg`;
					const showSiteLinks = !this.includes(hiddenDetails, `FOOTER`);
					let promise;

					this.render();

					promises.push(
						this.create({
							namespace: `Modules/PageHeader`,
							setup: {
								name: `page-header`,
								showTixrLogo: false,
								showUserLinks: false,
								leftLinks: [
									{
										type: `image`,
										alt: group.name,
										href: url,
										src: group.logoThumbnailUrl,
										desktop: true,
										app: false,
										mobile: false,
									},
									{
										type: `icon`,
										mobile: true,
										desktop: false,
										app: false,
										href: `/`,
										icon: `arrow-left`,
									},
								],
							},
						}),
						this.create({
							namespace: `Modules/PageFooter`,
							setup: {
								name: `page-footer`,
								showSiteLinks,
							},
						}),
						this.create({
							namespace: `Modules/ParallaxBlur`,
							setup: {
								name: `parallax-blur-desktop`,
								backgroundImageUrl: this.isString(backgroundUrl, 1) ? backgroundUrl : backupBackgroundUrl,
								scrollContainer: this.container,
							},
						}),
						this.create({
							namespace: `Modules/ParallaxBlur`,
							setup: {
								name: `parallax-blur-mobile`,
								backgroundImageUrl: this.isString(mobileBackgroundUrl, 1) ? mobileBackgroundUrl : backupBackgroundUrl,
								scrollContainer: this.container,
							},
						}),
						this.create({
							namespace: `Modules/Collapsible`,
							setup: {
								name: `description`,
								content: group.description,
							},
						})
					);

					data.expanding = false;

					switch (mode) {
						case `calendar`: // CALENDAR layout
							promise = this.create({
								namespace: `Modules/GroupCalendar`,
								setup: {
									name: `group-calendar`,
									groupId: group.id,
									showPrices,
								},
							});
							promises.push(promise);
							break;

						case `carousel`: {
							// CAROUSEL layout
							const { carouselConfiguration } = group;
							const { carousels } = carouselConfiguration;
							promise = this.create({
								namespace: `Modules/CarouselList`,
								setup: {
									carousels,
									mode: `ux3`,
									name: `carousel-list`,
									showAnchorLinks: true,
								},
							});

							promises.push(promise);
							break;
						}

						case `empty`:
							// no further setup required
							break;

						default:
							// FLYER, GRID, LIST layouts
							promise = this.create({
								namespace: `Modules/EventList`,
								setup: {
									name: `event-list`,
									layout: group.layout,
									showPrices: true, // not yet supported for non-CALENDAR layouts
									events,
								},
							});
							promises.push(promise);
					}

					return this.when({ promises });
				},
			});
		},
		ready(setup) {
			const { App, data } = this;
			const { group, moreEventsAvailable } = data;
			const { layout } = group;
			const isScrollingMode = this.includes(SCROLLING_LAYOUTS, layout);

			if (isScrollingMode && moreEventsAvailable) {
				App.onScroll(this.onScroll);

				this.defer({
					then: this.onScroll,
					duration: 100,
				});
			}
		},
		api: {
			getMoreEvents: {
				async: true,
				value() {
					const { Groups, data } = this;
					const { EVENTS_PER_PAGE, currentPage, group } = data;
					const [id, page] = [group.id, currentPage];
					const options = {
						id,
						block: false,
						then: (events) => {
							if (events.length < EVENTS_PER_PAGE) {
								data.moreEventsAvailable = false;
							}

							return events;
						},
					};

					if (this.isInt(currentPage)) {
						options.page = page;
						data.currentPage = page + 1;
					}

					return Groups.getEventsById(options);
				},
			},

			onScroll: {
				value(evt) {
					const { App, DOM, data, instances } = this;
					const { expanding, moreEventsAvailable } = data;

					if (!moreEventsAvailable) {
						App.offScroll(this.onScroll);
					}

					if (!moreEventsAvailable || expanding) {
						return;
					}

					const cushion = 50;
					const target = this.get(`.scroll-trigger`);
					const inViewport = DOM.inViewport({ cushion, target });

					if (inViewport) {
						data.expanding = true;

						return this.getMoreEvents({
							then: (events) => {
								const eventList = instances[`event-list`];
								eventList.addEvents({ events });

								return this.defer({
									duration: 100,
									then: () => (data.expanding = false),
								});
							},
						});
					}
				},
			},
		},
	});
});

define("Pages/Group.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Pages/Group/events"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"content\"> </div>";
},"useData":true});

this["Templates"]["Pages/Group/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"logo\"><img src=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroUrl") : stack1), depth0))
    + "\" alt=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "\"/></div> ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"hero-text\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroText") : stack1), depth0))
    + "</div> ";
},"5":function(container,depth0,helpers,partials,data) {
    return " <div class=\"content calendar transition-content\"> <div name=\"group-calendar\"></div> </div> ";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"carousel",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":1,"column":843},"end":{"line":1,"column":1294}}})) != null ? stack1 : "");
},"8":function(container,depth0,helpers,partials,data) {
    return " <div class=\"content carousels transition-content\"> <div name=\"carousel-list\"></div> </div> ";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"empty",{"name":"compare","hash":{},"fn":container.program(11, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":1,"column":967},"end":{"line":1,"column":1294}}})) != null ? stack1 : "");
},"11":function(container,depth0,helpers,partials,data) {
    return " <div class=\"content alert\"> <h1 class=\"transition-content\">No upcoming shows</h1> <h2 class=\"transition-content\">Watch this page for future events!</h2> </div> ";
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div class=\"content events "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"layout") : stack1), depth0))
    + " transition-content\"> <div name=\"event-list\"></div> </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"head-fixed hide-mobile\">  <h1>Tixr - "
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</h1> </div>  <div class=\"head\"> <div name=\"page-header\"></div> </div> <div name=\"parallax-blur-desktop\" class=\"hide-mobile\"></div> <div name=\"parallax-blur-mobile\" class=\"hide-desktop\"></div> <div name=\"background-gradient\"></div> <div class=\"body\"> <div class=\"content compact hero\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroUrl") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":409},"end":{"line":1,"column":514}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"heroText") : stack1),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":515},"end":{"line":1,"column":593}}})) != null ? stack1 : "")
    + " <div class=\"home-link hide-mobile\"> <a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"PRIMARY_DOMAIN") : depth0), depth0))
    + "/\" class=\"logo\">"
    + alias2(lookupProperty(helpers,"getIcon").call(alias3,"tixr",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":673},"end":{"line":1,"column":691}}}))
    + "<sup>tm</sup></a> </div> </div> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"mode") : depth0),"calendar",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":1,"column":723},"end":{"line":1,"column":1306}}})) != null ? stack1 : "")
    + " <div class=\"scroll-trigger\"></div> <div class=\"content centered description\"> <h2 class=\"transition-content\">Description</h2> <div name=\"description\" class=\"transition-content\"></div> </div> <div class=\"content footer\"> <div name=\"page-footer\"></div> </div> </div>";
},"useData":true});

this["Templates"]["Pages/Group/noEvents"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"content noEvents\"> <h3>No upcoming shows</h3> </div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Group.js.map